import * as React from "react";
import {
  Button,
  Checkbox,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
  Switch,
  useToast,
} from "@repo/ui";
import {
  ChevronDown,
  ClipboardList,
  Download,
  FileText,
  Package,
  Pill,
  Receipt,
  ShieldCheck,
  Table,
} from "lucide-react";
import { useApproval } from "@/context/approval-context";
import { handlePermissionError } from "@/lib/utils/handle-permission-error";
import { getUsers } from "@/lib/api/users";
import { getWholesalers } from "@/lib/api/wholesalers";
import { generateReport } from "@/lib/api/reports";
import { ApiClientError } from "@/lib/api/client";
import { logger } from "@/lib/utils/logger";
import type { ExportColumn } from "@/components/export-modal";
import type { ReportSource } from "@/lib/types/reports";
import {
  ReportFiltersPanel,
  type InventoryReportFilters,
  type DispenseReportFilters,
  type ReceiptReportFilters,
  type ActivityReportFilters,
  type AuditReportFilters,
  type ReportFilterState,
} from "./report-filters";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3005";

// ─── Column definitions ────────────────────────────────────────────────────

const REPORT_COLUMNS: Record<ReportSource, ExportColumn[]> = {
  inventory: [
    { id: "ndc", label: "NDC", required: true, defaultSelected: true },
    { id: "itemName", label: "Drug Name", defaultSelected: true },
    { id: "drugSchedule", label: "Drug Schedule", defaultSelected: true },
    { id: "currentStock", label: "Current Stock", defaultSelected: true },
    { id: "labeler", label: "Labeler", defaultSelected: false },
    { id: "dosageForm", label: "Dosage Form", defaultSelected: false },
  ],
  dispenses: [
    { id: "ndc", label: "NDC", required: true, defaultSelected: true },
    { id: "date", label: "Date", defaultSelected: true },
    { id: "itemName", label: "Drug Name", defaultSelected: true },
    { id: "quantity", label: "Quantity", defaultSelected: true },
    { id: "stockAfter", label: "Stock After", defaultSelected: true },
    { id: "rxNumber", label: "RX Number", defaultSelected: true },
    { id: "createdBy", label: "Created By", defaultSelected: true },
  ],
  receipts: [
    { id: "ndc", label: "NDC", required: true, defaultSelected: true },
    { id: "dateReceived", label: "Date Received", defaultSelected: true },
    { id: "itemName", label: "Drug Name", defaultSelected: true },
    { id: "quantity", label: "Received", defaultSelected: true },
    { id: "stockAfter", label: "Stock After", defaultSelected: true },
    { id: "wholesaler", label: "Wholesaler", defaultSelected: true },
    { id: "invoiceNumber", label: "Invoice #", defaultSelected: true },
    { id: "createdBy", label: "Created By", defaultSelected: true },
  ],
  activity: [
    { id: "ndc", label: "NDC", required: true, defaultSelected: true },
    { id: "dateTime", label: "Date & Time", defaultSelected: true },
    { id: "type", label: "Type", defaultSelected: true },
    { id: "itemName", label: "Drug Name", defaultSelected: true },
    { id: "quantity", label: "Quantity", defaultSelected: true },
    { id: "stockAfter", label: "Stock After", defaultSelected: true },
    { id: "reference", label: "Reference", defaultSelected: true },
    { id: "createdBy", label: "Created By", defaultSelected: true },
    { id: "notes", label: "Notes", defaultSelected: false },
  ],
  audit: [
    { id: "timestamp", label: "Timestamp", defaultSelected: true },
    { id: "action", label: "Action Type", defaultSelected: true },
    { id: "table", label: "Table", defaultSelected: true },
    { id: "userName", label: "User", defaultSelected: true },
    { id: "userRole", label: "Role", defaultSelected: true },
    { id: "justification", label: "Justification", defaultSelected: false },
    { id: "ipAddress", label: "IP Address", defaultSelected: false },
    { id: "oldValues", label: "Old Values", defaultSelected: false },
    { id: "newValues", label: "New Values", defaultSelected: false },
  ],
};

const SOURCE_ENDPOINTS: Record<ReportSource, string> = {
  inventory: "/inventory",
  dispenses: "/dispenses",
  receipts: "/receipts",
  activity: "/activity",
  audit: "/audit",
};

const SOURCE_DEFAULT_NAMES: Record<ReportSource, string> = {
  inventory: "Inventory Report",
  dispenses: "Dispense Report",
  receipts: "Receipt Report",
  activity: "Activity Report",
  audit: "Audit Log Report",
};

// ─── Helpers ──────────────────────────────────────────────────────────────

function getDefaultColumns(source: ReportSource): string[] {
  return REPORT_COLUMNS[source]
    .filter((c) => c.defaultSelected !== false)
    .map((c) => c.id);
}

function getDefaultFilters(source: ReportSource): ReportFilterState {
  switch (source) {
    case "inventory":
      return {} as InventoryReportFilters;
    case "dispenses":
      return {} as DispenseReportFilters;
    case "receipts":
      return {} as ReceiptReportFilters;
    case "activity":
      return {} as ActivityReportFilters;
    case "audit":
      return {} as AuditReportFilters;
  }
}

function buildApiFilters(
  source: ReportSource,
  filterState: ReportFilterState
): Record<string, string> {
  const result: Record<string, string> = {};

  const add = (key: string, value: string | undefined | null | boolean) => {
    if (value === undefined || value === null || value === "") return;
    if (typeof value === "boolean") {
      result[key] = value ? "true" : "false";
      return;
    }
    result[key] = String(value);
  };

  switch (source) {
    case "inventory": {
      const f = filterState as InventoryReportFilters;
      add("status", f.status);
      add("drug_type", f.drug_type);
      add("show_hidden", f.show_hidden);
      break;
    }
    case "dispenses": {
      const f = filterState as DispenseReportFilters;
      add("dateFrom", f.dateFrom);
      add("dateTo", f.dateTo);
      add("userIds", f.userIds);
      add("ndc", f.ndc);
      add("itemName", f.itemName);
      add("rxNumber", f.rxNumber);
      break;
    }
    case "receipts": {
      const f = filterState as ReceiptReportFilters;
      add("dateReceivedFrom", f.dateReceivedFrom);
      add("dateReceivedTo", f.dateReceivedTo);
      add("wholesalerId", f.wholesalerId);
      add("userIds", f.userIds);
      add("ndc", f.ndc);
      add("itemName", f.itemName);
      add("invoiceNumber", f.invoiceNumber);
      break;
    }
    case "activity": {
      const f = filterState as ActivityReportFilters;
      add("startDate", f.startDate);
      add("endDate", f.endDate);
      add("transactionType", f.transactionType);
      add("ndc", f.ndc);
      add("itemName", f.itemName);
      add("userIds", f.userIds);
      break;
    }
    case "audit": {
      const f = filterState as AuditReportFilters;
      add("action_timestampFrom", f.action_timestampFrom);
      add("action_timestampTo", f.action_timestampTo);
      add("action_type", f.action_type);
      add("table_name", f.table_name);
      add("user_id", f.user_id);
      break;
    }
  }

  return result;
}

function hasActiveFilters(
  source: ReportSource,
  filterState: ReportFilterState
): boolean {
  return Object.keys(buildApiFilters(source, filterState)).length > 0;
}

function computeDefaultName(source: ReportSource): string {
  const dateStr = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
  return `${SOURCE_DEFAULT_NAMES[source]} - ${dateStr}`;
}

// ─── Source config ─────────────────────────────────────────────────────────

const SOURCE_OPTIONS: {
  value: ReportSource;
  label: string;
  description: string;
  icon: React.ElementType;
}[] = [
  {
    value: "inventory",
    label: "Inventory",
    description: "Current stock levels and drug details",
    icon: Package,
  },
  {
    value: "dispenses",
    label: "Dispenses",
    description: "Dispensing records and RX details",
    icon: Pill,
  },
  {
    value: "receipts",
    label: "Receipts",
    description: "Purchase receipts and invoices",
    icon: Receipt,
  },
  {
    value: "activity",
    label: "Activity",
    description: "All transactions and stock changes",
    icon: ClipboardList,
  },
  {
    value: "audit",
    label: "Audit Log",
    description: "User actions and system changes",
    icon: ShieldCheck,
  },
];

// ─── Format config ────────────────────────────────────────────────────────

const FORMAT_META = {
  pdf: {
    label: "PDF",
    description: "Formatted report with pharmacy header",
    icon: FileText,
  },
  csv: {
    label: "CSV",
    description: "Spreadsheet-compatible format",
    icon: Table,
  },
} as const;

type ExportFormat = "pdf" | "csv";

// ─── User / Wholesaler types ──────────────────────────────────────────────

interface User {
  id: string;
  initials: string;
  full_name: string | null;
  role: string;
}

interface Wholesaler {
  id: string;
  name: string;
}

// ─── Section wrapper ──────────────────────────────────────────────────────

function Section({
  number,
  title,
  subtitle,
  defaultOpen = true,
  children,
}: {
  number: number;
  title: string;
  subtitle?: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = React.useState(defaultOpen);

  return (
    <div>
      <button
        type="button"
        className="flex w-full items-center gap-3 py-3"
        onClick={() => setOpen(!open)}
      >
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-foreground/10 text-xs font-semibold">
          {number}
        </span>
        <div className="flex-1 text-left">
          <span className="text-sm font-semibold">{title}</span>
          {subtitle && (
            <span className="ml-2 text-xs text-muted-foreground">
              {subtitle}
            </span>
          )}
        </div>
        <ChevronDown
          className={`h-4 w-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && <div className="pb-4 pl-9">{children}</div>}
    </div>
  );
}

// ─── Props ────────────────────────────────────────────────────────────────

interface ReportGeneratorProps {
  onGenerated?: () => void;
}

// ─── Component ───────────────────────────────────────────────────────────

export function ReportGenerator({ onGenerated }: ReportGeneratorProps) {
  const { toast } = useToast();
  const { requestApproval } = useApproval();

  // ── Report type ──────────────────────────────────────────────────────
  const [reportType, setReportType] = React.useState<ReportSource>("inventory");

  // ── Format ───────────────────────────────────────────────────────────
  const [format, setFormat] = React.useState<ExportFormat>("pdf");
  const [includeFiltersSummary, setIncludeFiltersSummary] =
    React.useState(true);

  // ── Per-source column selections (persists across tab switches) ──────
  const [columnSelections, setColumnSelections] = React.useState<
    Record<ReportSource, string[]>
  >({
    inventory: getDefaultColumns("inventory"),
    dispenses: getDefaultColumns("dispenses"),
    receipts: getDefaultColumns("receipts"),
    activity: getDefaultColumns("activity"),
    audit: getDefaultColumns("audit"),
  });

  // ── Per-source filter states (persists across tab switches) ──────────
  const [filterStates, setFilterStates] = React.useState<
    Record<ReportSource, ReportFilterState>
  >({
    inventory: getDefaultFilters("inventory"),
    dispenses: getDefaultFilters("dispenses"),
    receipts: getDefaultFilters("receipts"),
    activity: getDefaultFilters("activity"),
    audit: getDefaultFilters("audit"),
  });

  // ── Report name ──────────────────────────────────────────────────────
  const [reportName, setReportName] = React.useState("");

  // ── Generate state ───────────────────────────────────────────────────
  const [isGenerating, setIsGenerating] = React.useState(false);

  // ── Record count ─────────────────────────────────────────────────────
  const [recordCount, setRecordCount] = React.useState<number | null>(null);
  const [isCountLoading, setIsCountLoading] = React.useState(false);

  // ── Async data ───────────────────────────────────────────────────────
  const [users, setUsers] = React.useState<User[]>([]);
  const [wholesalers, setWholesalers] = React.useState<Wholesaler[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = React.useState(true);
  const [isLoadingWholesalers, setIsLoadingWholesalers] = React.useState(true);

  React.useEffect(() => {
    getUsers({ pageSize: 200 })
      .then((result) => {
        setUsers(result.data?.users ?? result.users ?? []);
      })
      .catch((err) => {
        logger.error("Fetch users for report failed", err, {
          operation: "report_fetch_users",
        });
        setUsers([]);
      })
      .finally(() => setIsLoadingUsers(false));

    getWholesalers({ pageSize: 200 })
      .then((result) => {
        setWholesalers(result.data?.wholesalers ?? result.wholesalers ?? []);
      })
      .catch((err) => {
        logger.error("Fetch wholesalers for report failed", err, {
          operation: "report_fetch_wholesalers",
        });
        setWholesalers([]);
      })
      .finally(() => setIsLoadingWholesalers(false));
  }, []);

  // ── Count fetch ──────────────────────────────────────────────────────

  const filterKey = JSON.stringify(filterStates[reportType]);

  React.useEffect(() => {
    const controller = new AbortController();
    setIsCountLoading(true);
    setRecordCount(null);

    const filters = buildApiFilters(reportType, filterStates[reportType]);
    const params = new URLSearchParams({ pageSize: "1", page: "1" });
    Object.entries(filters).forEach(([k, v]) => params.append(k, v));

    fetch(`${API_URL}${SOURCE_ENDPOINTS[reportType]}?${params}`, {
      signal: controller.signal,
      credentials: "include",
    })
      .then((r) => r.json())
      .then((result) => {
        const total =
          result.data?.pagination?.total ?? result.pagination?.total ?? null;
        setRecordCount(total);
      })
      .catch((err: unknown) => {
        if ((err as Error).name !== "AbortError") {
          logger.error("Fetch report record count failed", err, {
            operation: "report_fetch_count",
            metadata: { reportType },
          });
        }
      })
      .finally(() => setIsCountLoading(false));

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportType, filterKey]);

  // ── Derived ──────────────────────────────────────────────────────────

  const currentColumns = REPORT_COLUMNS[reportType];
  const selectedColumns = columnSelections[reportType];
  const currentFilterState = filterStates[reportType];
  const activeFilters = hasActiveFilters(reportType, currentFilterState);
  const isPdf = format === "pdf";

  const computedDefaultName = computeDefaultName(reportType);
  const effectiveName = reportName.trim() || computedDefaultName;

  // ── Column helpers ───────────────────────────────────────────────────

  const toggleColumn = (id: string) => {
    const col = currentColumns.find((c) => c.id === id);
    if (col?.required) return;
    setColumnSelections((prev) => {
      const current = prev[reportType];
      const next = current.includes(id)
        ? current.filter((c) => c !== id)
        : [...current, id];
      return { ...prev, [reportType]: next };
    });
  };

  const allColumnsSelected = selectedColumns.length === currentColumns.length;

  const toggleAllColumns = () => {
    setColumnSelections((prev) => ({
      ...prev,
      [reportType]: allColumnsSelected
        ? currentColumns.filter((c) => c.required).map((c) => c.id)
        : currentColumns.map((c) => c.id),
    }));
  };

  // ── Filter helper ────────────────────────────────────────────────────

  const updateFilters = (source: ReportSource, updates: ReportFilterState) => {
    setFilterStates((prev) => ({ ...prev, [source]: updates }));
  };

  // ── Generate handler ─────────────────────────────────────────────────

  const handleGenerate = async (approvalToken?: string) => {
    setIsGenerating(true);
    try {
      const filters = buildApiFilters(reportType, currentFilterState);

      await generateReport({
        source: reportType,
        format,
        reportName: effectiveName,
        columns: selectedColumns,
        filters,
        includePharmacyHeader: isPdf,
        includeFiltersSummary: isPdf ? includeFiltersSummary : false,
        ...(approvalToken ? { approvalToken } : {}),
      });

      setReportName("");
      toast({
        title: "Report saved",
        description: `"${effectiveName}" is ready in Saved Reports below.`,
        variant: "success",
      });
      onGenerated?.();
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (
          handlePermissionError(err, {
            requestApproval,
            toast,
            retryFn: async (token) => handleGenerate(token),
            actionDescription: "Generate report",
          })
        )
          return;
      }
      logger.error("Generate report failed", err, {
        operation: "generate_report",
        metadata: { reportType },
      });
      toast({
        title: "Generation Failed",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────

  const activeFilterCount = Object.keys(
    buildApiFilters(reportType, currentFilterState)
  ).length;

  return (
    <div className="rounded-xl border border-border/60 bg-surface">
      {/* ── Section 1: Report Source ─────────────────────────────────── */}
      <div className="px-5 pt-4">
        <Section
          number={1}
          title="Data Source"
          subtitle="What do you want to report on?"
        >
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
            {SOURCE_OPTIONS.map((option) => {
              const Icon = option.icon;
              const isActive = reportType === option.value;
              return (
                <button
                  key={option.value}
                  data-testid={`report-source-${option.value}`}
                  className={`flex flex-col items-start gap-1 rounded-lg border p-3 text-left transition-colors ${
                    isActive
                      ? "border-primary/40 bg-primary/5"
                      : "border-border hover:bg-muted/30"
                  }`}
                  onClick={() => setReportType(option.value)}
                >
                  <Icon
                    className={`h-4 w-4 ${isActive ? "text-primary" : "text-muted-foreground"}`}
                  />
                  <span className="text-sm font-medium">{option.label}</span>
                  <span className="text-[11px] text-muted-foreground leading-tight">
                    {option.description}
                  </span>
                </button>
              );
            })}
          </div>
        </Section>
      </div>

      <div className="mx-5 border-t border-border/60" />

      {/* ── Section 2: Filters ───────────────────────────────────────── */}
      <div className="px-5">
        <Section
          number={2}
          title="Filters"
          subtitle={
            activeFilterCount > 0
              ? `${activeFilterCount} active`
              : "Optional — narrow your results"
          }
        >
          <div className="rounded-lg border border-border/60 bg-muted/10 p-4">
            <ReportFiltersPanel
              source={reportType}
              filters={filterStates[reportType] as never}
              onChange={(f: ReportFilterState) => updateFilters(reportType, f)}
              users={users}
              wholesalers={wholesalers}
              isLoadingUsers={isLoadingUsers}
              isLoadingWholesalers={isLoadingWholesalers}
            />
          </div>

          {/* Record count */}
          <div className="mt-3 text-sm text-muted-foreground">
            {isCountLoading ? (
              <span>Counting records...</span>
            ) : recordCount !== null ? (
              <span>
                <span className="font-semibold text-foreground">
                  {recordCount.toLocaleString()}
                </span>{" "}
                {recordCount === 1 ? "record" : "records"} match your filters
              </span>
            ) : null}
          </div>
        </Section>
      </div>

      <div className="mx-5 border-t border-border/60" />

      {/* ── Section 3: Columns ───────────────────────────────────────── */}
      <div className="px-5">
        <Section
          number={3}
          title="Columns"
          subtitle={`${selectedColumns.length}/${currentColumns.length} selected`}
        >
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs text-muted-foreground">
              Click columns to toggle them on or off. NDC is always included.
            </p>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs"
              onClick={toggleAllColumns}
            >
              {allColumnsSelected ? "Deselect all" : "Select all"}
            </Button>
          </div>

          {/* Spreadsheet-style column preview */}
          <div className="rounded-lg border border-border/60 overflow-hidden">
            {/* Header row — the toggleable columns */}
            <div className="flex overflow-x-auto bg-muted/30">
              {currentColumns.map((col) => {
                const isChecked = selectedColumns.includes(col.id);
                return (
                  <button
                    key={col.id}
                    type="button"
                    disabled={col.required}
                    onClick={() => toggleColumn(col.id)}
                    className={`flex-1 min-w-[100px] px-3 py-2.5 text-xs font-semibold text-left border-r border-border/40 last:border-r-0 transition-colors ${
                      isChecked
                        ? "text-foreground bg-muted/40"
                        : "text-muted-foreground/40 bg-muted/10 line-through"
                    } ${col.required ? "cursor-default" : "cursor-pointer hover:bg-muted/50"}`}
                  >
                    <div className="flex items-center gap-1.5">
                      <Checkbox
                        checked={isChecked}
                        tabIndex={-1}
                        disabled={col.required}
                        className="h-3.5 w-3.5 shrink-0 pointer-events-none [&_svg]:h-3 [&_svg]:w-3"
                      />
                      <span>{col.label}</span>
                    </div>
                    {col.required && (
                      <span className="text-[9px] font-normal text-muted-foreground mt-0.5 block">
                        Required
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            {/* Sample data rows — just visual placeholders */}
            {[0, 1, 2].map((row) => (
              <div
                key={row}
                className={`flex border-t border-border/30 ${row === 2 ? "opacity-40" : row === 1 ? "opacity-60" : ""}`}
              >
                {currentColumns.map((col) => {
                  const isChecked = selectedColumns.includes(col.id);
                  return (
                    <div
                      key={col.id}
                      className={`flex-1 min-w-[100px] px-3 py-2 border-r border-border/20 last:border-r-0 ${
                        isChecked ? "" : "opacity-20"
                      }`}
                    >
                      <div className="h-2.5 w-3/4 rounded bg-muted-foreground/10" />
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </Section>
      </div>

      <div className="mx-5 border-t border-border/60" />

      {/* ── Section 4: Output ────────────────────────────────────────── */}
      <div className="px-5 pb-5">
        <Section number={4} title="Output" subtitle="Format and naming">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            {/* Format */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Format
              </Label>
              <RadioGroup
                value={format}
                onValueChange={(v) => setFormat(v as ExportFormat)}
                className="flex gap-2"
              >
                {(Object.keys(FORMAT_META) as ExportFormat[]).map((f) => {
                  const meta = FORMAT_META[f];
                  const Icon = meta.icon;
                  return (
                    <label
                      key={f}
                      className={`flex flex-1 items-center gap-2.5 rounded-lg border p-3 cursor-pointer transition-colors ${
                        format === f
                          ? "border-primary/40 bg-primary/5"
                          : "border-border hover:bg-muted/30"
                      }`}
                    >
                      <RadioGroupItem value={f} id={`format-${f}`} />
                      <Icon
                        className={`h-4 w-4 ${
                          format === f
                            ? "text-primary"
                            : "text-muted-foreground"
                        }`}
                      />
                      <div className="min-w-0">
                        <span className="text-sm font-medium">
                          {meta.label}
                        </span>
                        <p className="text-[11px] text-muted-foreground">
                          {meta.description}
                        </p>
                      </div>
                    </label>
                  );
                })}
              </RadioGroup>

              {isPdf && activeFilters && (
                <div className="flex items-center justify-between rounded-lg border border-border/60 px-3 py-2">
                  <Label
                    htmlFor="filters-summary"
                    className="text-sm cursor-pointer"
                  >
                    Include filters summary in PDF
                  </Label>
                  <Switch
                    id="filters-summary"
                    checked={includeFiltersSummary}
                    onCheckedChange={setIncludeFiltersSummary}
                  />
                </div>
              )}
            </div>

            {/* Name + Generate */}
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label
                  htmlFor="report-name"
                  className="text-xs font-semibold text-muted-foreground uppercase tracking-wide"
                >
                  Report Name
                </Label>
                <Input
                  id="report-name"
                  data-testid="report-name-input"
                  value={reportName}
                  onChange={(e) => setReportName(e.target.value)}
                  placeholder={computedDefaultName}
                  className="h-9"
                />
              </div>

              <Button
                className="w-full gap-2"
                data-testid="report-generate-submit"
                onClick={() => handleGenerate()}
                disabled={isGenerating || selectedColumns.length === 0}
              >
                {isGenerating ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4" />
                    Generate Report
                  </>
                )}
              </Button>
            </div>
          </div>
        </Section>
      </div>
    </div>
  );
}
